import { useEffect, useRef, useState } from 'react'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { voxelsCandidates, fetchFirstOk } from '../lib/navmeshGridKey'
import { navSettingsStore } from '../lib/navSettingsStore'

/**
 * VoxelVolume — the granular voxel-surface debug layer (ENG-591). Loads the
 * baked `.voxels.json` + `.voxels.bin` (decoded from splat-transform's sparse
 * voxel octree by tools/voxel_to_cubes.mjs) and draws one cube per surface
 * voxel via a single InstancedMesh. This is the cube grid the splat-transform
 * COLLISION guide shows — distinct from the merged `-K smooth` collision mesh,
 * which reads as big flat panels.
 *
 * Engine space → Spark world via the constant Rz(180°) (group.rotation.z=π),
 * matching NavMeshVolume's collision-mesh placement.
 *
 * No silent fallback (CLAUDE.md): a missing artifact surfaces as
 * window.__VOXELS__.state === 'no-cache' with a console pointer — never faked.
 */
export default function VoxelVolume({ splatUrl, splatVersion, reloadKey = 0 }) {
  const scene = useThree(s => s.scene)
  const groupRef = useRef(null)
  const [, force] = useState(0)
  useEffect(() => navSettingsStore.subscribe(() => force(n => n + 1)), [])
  // Cubes are an authoring aid — only while the Build Navmesh panel is open.
  const { show } = navSettingsStore.getState()

  useEffect(() => {
    if (!splatUrl || !scene) return
    let cancelled = false
    let group = null
    const setState = (state, extra) => { if (typeof window !== 'undefined') window.__VOXELS__ = { state, ...extra } }
    setState('pending')

    ;(async () => {
      const bust = splatVersion ? `?v=${encodeURIComponent(splatVersion)}` : ''
      const { url: metaUrl, res: metaRes } = await fetchFirstOk(await voxelsCandidates(splatUrl), bust)
      const ct = metaRes?.headers.get('content-type') || ''
      if (!metaRes || !metaRes.ok || /html/.test(ct)) {
        console.warn(`[VoxelVolume] no baked voxels for ${splatUrl}. The bake service writes baked/voxels.json beside the .ply.`)
        setState('no-cache', { url: metaUrl })
        return
      }
      const meta = await metaRes.json()
      // Derive the .bin URL from the .json URL. Match WITHOUT a leading dot so
      // it works for both the co-located server path (`baked/voxels.json`, slash
      // before "voxels") and the local keyed path (`<key>.voxels.json`, dot
      // before "voxels"). The old `.voxels.json` pattern only matched the local
      // form, so the server `.bin` silently re-fetched the 77-byte .json.
      // 'no-cache' = ETag revalidation, matching fetchFirstOk — otherwise the
      // .bin could pair a fresh .json with a day-old cached body.
      const binRes = await fetch(metaUrl.replace('voxels.json', 'voxels.bin') + bust, { cache: 'no-cache' })
      if (!binRes.ok) { setState('error', { error: 'voxels.bin missing' }); return }
      const ab = await binRes.arrayBuffer()
      if (cancelled) return
      // voxels.bin is int16 triples (6 bytes/cell). Defend against a truncated /
      // partial fetch (e.g. grabbed mid-bake): floor to whole cells so a short
      // read degrades gracefully instead of throwing on a non-even byte length.
      const usableCells = Math.floor(ab.byteLength / 6)
      if (usableCells * 6 !== ab.byteLength) {
        console.warn(`[VoxelVolume] voxels.bin length ${ab.byteLength} not a whole number of cells; using ${usableCells}`)
      }
      const coords = new Int16Array(ab, 0, usableCells * 3)

      const count = usableCells
      const res = meta.res
      // `origin` is the world-space grid corner (Go bake). `originEngine` is the
      // legacy splat-transform field (engine space, needed the Rz180 below);
      // support both so older artifacts still load. `worldSpace` gates the rotation.
      const worldSpace = meta.origin != null
      const [ox, oy, oz] = meta.origin ?? meta.originEngine
      const geom = new THREE.BoxGeometry(res * 0.96, res * 0.96, res * 0.96)
      const mat = new THREE.MeshStandardMaterial({
        color: 0xff8030, transparent: true, opacity: 0.7, flatShading: true,
        roughness: 0.85, metalness: 0.0, emissive: 0x4a2400, emissiveIntensity: 0.35,
      })
      const inst = new THREE.InstancedMesh(geom, mat, count)
      const m = new THREE.Matrix4()
      for (let k = 0; k < count; k++) {
        // Voxel center = origin + (g + 0.5) * res.
        m.makeTranslation(
          ox + (coords[k * 3] + 0.5) * res,
          oy + (coords[k * 3 + 1] + 0.5) * res,
          oz + (coords[k * 3 + 2] + 0.5) * res,
        )
        inst.setMatrixAt(k, m)
      }
      inst.instanceMatrix.needsUpdate = true

      group = new THREE.Group()
      if (!worldSpace) group.rotation.z = Math.PI // legacy engine → Spark world
      group.add(inst)
      const ns = navSettingsStore.getState()
      group.visible = ns.show.voxels
      scene.add(group)
      groupRef.current = group
      if (typeof window !== 'undefined') window.__VOXELS__ = { state: 'applied', url: metaUrl, count }
      console.log(`[VoxelVolume] applied ${count} voxel cubes from ${metaUrl}`)
    })().catch(err => {
      if (cancelled) return
      console.error('[VoxelVolume] failed:', err)
      setState('error', { error: String(err) })
    })

    return () => {
      cancelled = true
      if (group) { scene.remove(group); group.traverse(o => { o.geometry?.dispose?.(); o.material?.dispose?.() }) }
      groupRef.current = null
      if (typeof window !== 'undefined') delete window.__VOXELS__
    }
    // reloadKey: re-fetch when a bake for this env completes (bakeReload.js).
  }, [splatUrl, splatVersion, scene, reloadKey])

  // Live-toggle visibility from the panel checkbox + open state, no reload.
  useEffect(() => {
    if (groupRef.current) groupRef.current.visible = show.voxels
  }, [show.voxels])

  return null
}
